import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";

/**
 * DashboardGreeting — V1.1.1 OP-002 · Dashboard Productivity.
 *
 * A single-line greeting at the top of the Dashboard so office staff feel
 * the platform is "theirs" the moment they land. Shows:
 *   • Good Morning / Afternoon / Evening (based on local wall-clock)
 *   • The signed-in user's first name (or full name fallback)
 *   • Today's date (locale-aware, en-CA)
 *   • Live-updating current time (ticks every 30 seconds so nothing
 *     drifts more than 30s from real wall-clock)
 *
 * Timezone: reads the browser's local time. This is correct for a
 * dispatcher sitting in front of the app — nothing in this greeting is
 * persisted server-side.
 */
export default function DashboardGreeting() {
  const { user } = useAuth();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(tick);
  }, []);

  const hour = now.getHours();
  const greeting =
    hour < 12 ? "Good Morning" :
    hour < 17 ? "Good Afternoon" : "Good Evening";

  const firstName = (user?.full_name || "").split(" ")[0] || null;
  const dateStr = now.toLocaleDateString("en-CA", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  const timeStr = now.toLocaleTimeString("en-CA", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });

  return (
    <div
      data-testid="dashboard-greeting"
      className="flex flex-wrap items-baseline justify-between gap-2 rounded-md border border-divider bg-surface px-4 py-3"
    >
      <div>
        <div className="font-display text-base font-semibold text-ink-primary">
          <span data-testid="dashboard-greeting-text">{greeting}</span>
          {firstName && (
            <>
              , <span data-testid="dashboard-greeting-name">{firstName}</span>
            </>
          )}
        </div>
        <div className="mt-0.5 text-xs text-ink-secondary">
          {user?.role && (
            <span className="mr-2 rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink-secondary">
              {user.role}
            </span>
          )}
          <span data-testid="dashboard-greeting-date">{dateStr}</span>
        </div>
      </div>
      <div
        data-testid="dashboard-greeting-time"
        className="font-mono text-lg font-semibold text-ink-primary tabular-nums"
      >
        {timeStr}
      </div>
    </div>
  );
}
